<template>
  <div class="qty-component d-flex justify-content-around">
    <button class="rmv-qty-btn text-white" :disabled="isManualWeightLocked" @click="removeFromCart">
      <font-awesome-icon icon="fa-solid fa-minus"/>
    </button>
    <input class="qty-input" v-model="saleLine.Qty" @input="recalculateTip" type="number" :readonly="isReadOnly" :disabled="isManualWeightLocked">
    <button class="add-qty-btn text-white" :disabled="isManualWeightLocked" @click="addToCart">
      <font-awesome-icon icon="fa-solid fa-plus"/>
    </button>
    <small v-if="isManualWeightLocked" class="qty-locked-note text-warning">
      Manual weight entry is disabled for this item. Use Read Scale instead.
    </small>
  </div>
</template>

<script>
// docs/FRONTEND.md — sale line Qty contract; qty-reset-on-save fix
// Reference: MobileCartView.incrementQuantity → updateSaleLineQty (sales-module ~L807)
import { nextInPlaceQty } from '@/utils/saleLineQty';

export default {
  name: "CurrentSaleQty",
  props: {
    saleLine: {
      type: Object,
      required: true
    }
  },
  computed : {
    deviceSettings(){
      return this.$store.getters.getDeviceSettings;
    },
    isReadOnly(){
      if (!this.deviceSettings) return true;
      return !this.deviceSettings.SelectQty || this.isManualWeightLocked;
    },
  },
  data() {
    return {
      isManualWeightLocked: false,
    };
  },
  async mounted() {
    await this.refreshManualWeightLock();
  },
  watch: {
    'saleLine.Plu': {
      handler() {
        this.refreshManualWeightLock();
      },
      immediate: true,
    },
  },
  methods: {
    /**
     * Resolve whether this PLU is a scale item without manual-weight permission.
     * @returns {Promise<void>}
     */
    async refreshManualWeightLock() {
      try {
        const canManualWeightEntry = await this.$store.dispatch('validateSecurityPermissionNoPrompt', 'manualWeightEntry');
        const set = await this.$store.dispatch('getStockSetting', this.saleLine?.Plu);
        this.isManualWeightLocked = Boolean(set?.scale || set?.Scale) && !canManualWeightEntry;
      } catch (err) {
        console.warn('[CurrentSaleQty] Failed to resolve manual weight lock state', err);
        this.isManualWeightLocked = false;
      }
    },
    /**
     * +1 on an existing POS cart line — in-place qty update via updateSaleLineQty.
     *
     * Previously called addToCurrentSaleLines with Qty:1, which either merged
     * incorrectly (Unit.toFixed drift) or left math that saved qty=1
     * (qty-reset-on-save loss vector #2). Matches MobileCartView.incrementQuantity.
     *
     * Cooking-link items still navigate to the instruction screen so the
     * operator can attach modifiers; plain lines bump qty in place.
     *
     * @returns {Promise<*>|void}
     */
    addToCart() {
      if (this.isManualWeightLocked) {
        responses.showWarning('Manual quantity entry is disabled for scale items on your account. Use Read Scale instead.');
        return;
      }

      return this.$store.dispatch('getStockSetting', this.saleLine.Plu)
          .then(async (set) => {
            let cookingLink = 0;
            if (set) {
              cookingLink = set.cookingLink;
            }

            // Cooking-link PLUs: open instruction picker (separate line with modifiers)
            if (cookingLink) {
              return this.$router.push({
                name: 'instruction',
                query: {
                  plu: this.saleLine.Plu
                }
              });
            }

            // In-place +1 — never re-add with Qty:1
            const newQty = nextInPlaceQty(this.saleLine);
            this.$store.commit('updateSaleLineQty', {
              saleLine: this.saleLine,
              qty: newQty,
            });
            return this.recalculateTip();
          })
    },

    async recalculateTip(){
      await this.$store.dispatch(`recalculateVat`)
      return this.$store.dispatch('calculateTipAmount')
    },

    removeFromCart() {
      if (this.isManualWeightLocked) {
        responses.showWarning('Manual quantity entry is disabled for scale items on your account. Use Read Scale instead.');
        return;
      }

      this.$store.commit(`setOverrideDetails`,`Void Item (${this.saleLine.Description})`)
      return this.$store.commit("voidItem", this.saleLine);
    },

    askForPrice(item) {
      return Swal.fire({
        allowOutsideClick: false,
        showCancelButton: true,
        showLoaderOnConfirm: true,
        title: `Set Selling Price`,
        html: `<h6 >Description : ${item.Description}</h6>
               <h6 >Current Price : ${item.Unit.toFixed(2)}</h6>
               <input type="number" step="0.01" id="selling-prc" class="swal2-input" placeholder="Selling Price" style="max-width: 70%">`,
        confirmButtonText: 'Set Price',
        focusConfirm: false,
        preConfirm: async () => {
          let value = Swal.getPopup().querySelector('#selling-prc').value
          if (!value) {
            Swal.showValidationMessage(`Please enter a valid Selling Price`)
          }
          return parseFloat(value);
        }
      }).then(result=>{
        if (result.isConfirmed){
          return result.value;
        }
        return parseFloat(item.SellingIncl.toFixed(2))
      })
    },
  }
}
</script>

<style scoped>
.qty-component {
  gap: 5px;
}

.qty-input {
  width: 50px;
  border: 1px solid #C9CACF;
  border-radius: 10px;
  text-align: center;
}

.add-qty-btn, .rmv-qty-btn {
  border-radius: 10px;
  width: 30px;
  height: 30px;
}

.qty-locked-note {
  display: block;
  width: 100%;
  text-align: center;
  line-height: 1.2;
}

button {
  background: #0092f7;
}
</style>
